/* /reset-password?token=[&invite=1] — choose a new password (reset link) or set the first password (invitation). */
import "../account/account.css";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useResetPassword } from "../../data/hooks";
import { ApiError } from "../../data/provider";
import { useI18n } from "../../i18n";
import { Alert, Button, Icon, errMsg } from "../../ui";
import { fieldMsg } from "../account/messages";
import PasswordField from "../account/PasswordField";
import { passwordProblem } from "../account/passwordPolicy";
import { AuthSide, AuthTop } from "./components/AuthLayout";

export default function ResetPassword() {
  const { L, t } = useI18n();
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const invite = sp.get("invite") === "1";
  const m = useResetPassword();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fe, setFe] = useState<{ pw?: string; confirm?: string }>({});
  const [err, setErr] = useState<string | null>(token ? null : L("This link is missing its token.", "الرابط ناقص."));
  const [doneEmail, setDoneEmail] = useState<string | null>(null);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setErr(null);
    const pp = passwordProblem(pw);
    const e = { pw: pp ? fieldMsg(L, pp) : undefined, confirm: !pp && confirm !== pw ? L("The passwords do not match", "كلمتا المرور غير متطابقتين") : undefined };
    setFe(e);
    if (e.pw || e.confirm) return;
    try { setDoneEmail((await m.mutateAsync({ token, password: pw })).email); }
    catch (x) {
      if (x instanceof ApiError && x.fields?.token) setErr(L("This link is invalid, already used or expired. Request a new one.", "الرابط غير صالح أو مستخدم أو منتهي الصلاحية. اطلب رابطًا جديدًا."));
      else if (x instanceof ApiError && x.fields?.password) setFe({ pw: fieldMsg(L, x.fields.password) });
      else setErr(errMsg(x));
    }
  };

  const title = invite ? L("Set your password", "تعيين كلمة المرور") : L("Choose a new password", "اختر كلمة مرور جديدة");
  return (
    <div className="auth" data-testid="reset-password">
      <AuthSide title={invite ? L("Welcome to your self-service.", "مرحبًا بك في الخدمة الذاتية.") : L("A fresh start for your account.", "بداية جديدة لحسابك.")}
        text={invite ? L("Payslips, leave, letters and attendance — all from your phone.", "القسائم والإجازات والخطابات والحضور — كلها من جوالك.") : L("After the change you'll be signed out everywhere, then you can sign in with the new password.", "بعد التغيير سيتم تسجيل خروجك من كل الأجهزة، ثم يمكنك الدخول بكلمة المرور الجديدة.")} />
      <main className="auth-main no-tabbar">
        <AuthTop />
        <div className="auth-card">
          {doneEmail ? (
            <div className="stack" data-testid="reset-done">
              <div className="inbox-hero"><span className="ico"><Icon name="check" /></span><h1>{invite ? L("You're all set", "تم بنجاح") : L("Password changed", "تم تغيير كلمة المرور")}</h1>
                <p className="muted" style={{ margin: 0 }}>{L("Sign in with ", "سجّل الدخول باستخدام ")}<b dir="ltr">{doneEmail}</b>{L(" and your new password.", " وكلمة المرور الجديدة.")}</p></div>
              <Link className="btn pri block" to={"/login?email=" + encodeURIComponent(doneEmail)} data-testid="reset-signin"><span>{t("Sign in")}</span></Link>
            </div>
          ) : (
            <form className="stack" onSubmit={submit} noValidate>
              <div><h1 style={{ fontSize: 26 }}>{title}</h1><p className="muted" style={{ margin: "6px 0 0" }}>{L("Use at least 8 characters with letters and digits.", "استخدم 8 أحرف على الأقل تتضمن حروفًا وأرقامًا.")}</p></div>
              {err && <Alert tone="bad" title={err} testId="reset-error" action={<Link className="btn sm" to="/forgot-password">{L("New link", "رابط جديد")}</Link>} />}
              <PasswordField label={L("New password", "كلمة المرور الجديدة")} value={pw} onChange={setPw} err={fe.pw} testId="reset-password-input" />
              <PasswordField label={L("Confirm password", "تأكيد كلمة المرور")} value={confirm} onChange={setConfirm} err={fe.confirm} testId="reset-confirm" meter={false} />
              <Button type="submit" variant="pri" className="block" loading={m.isPending} disabled={!token} data-testid="reset-submit">{title}</Button>
              <p className="sm muted" style={{ textAlign: "center", margin: 0 }}><Link to="/login">{L("Back to sign in", "العودة لتسجيل الدخول")}</Link></p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
